import { Colors, Pills, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { EditableAdjustmentCells } from './EditableAdjustmentCells';
import { EmployeeIdentityCell } from './EmployeeIdentityCell';
import { PublishedAdjustmentCells } from './PublishedAdjustmentCells';
import { SAVE_DEBOUNCE_MS } from '../calibrationConstants';
import { TABLE_CELL } from '../calibrationStyles';
import { formatScore } from '../utils/formatScore';
import { useDebouncedSave } from '../utils/useDebouncedSave';

import type { CalibrationEmployee, CalibrationPerformanceLevel } from '@/types/calibration';

import { useSaveCalibrationAdjustment } from '@/api/calibration';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';
import { levelForScore } from '@/utils/performanceScoreLevel';
import { getFullName } from '@/utils/user';

export interface EmployeeRowProps {
    employee: CalibrationEmployee;
    managerName: string;
    /** Empty when the company carries no job title for this employee. */
    jobTitle: string;
    levels: CalibrationPerformanceLevel[];
    cycleEvaluationId: string;
    language: string;
}

interface PendingAdjustment {
    calibratedScore: number | null;
    calibratedPerformanceLevelId: string | null;
    comment: string;
}

const toPendingAdjustment = ({ adjustment, score }: CalibrationEmployee): PendingAdjustment => ({
    calibratedScore: adjustment?.calibratedScore ?? score,
    calibratedPerformanceLevelId: adjustment?.calibratedPerformanceLevelId ?? null,
    comment: adjustment?.comment ?? '',
});

const isSameAdjustment = (first: PendingAdjustment, second: PendingAdjustment): boolean =>
    first.calibratedScore === second.calibratedScore &&
    first.calibratedPerformanceLevelId === second.calibratedPerformanceLevelId &&
    first.comment === second.comment;

/** One employee's calibration row: identity, submitted score and the level the manager gave
 * (read-only), then the calibrated score/level/comment — editable while the adjustment is a draft
 * (or absent), read-only once published, so a published row shows no dead editable field. */
export const EmployeeRow = ({
    employee,
    managerName,
    jobTitle,
    levels,
    cycleEvaluationId,
    language,
}: EmployeeRowProps) => {
    const { t } = useAmelioTranslations();

    const employeeName = getFullName(employee);

    const isPublished = employee.adjustment?.status === 'Published';
    const givenLevel = levels.find((level) => level.id === employee.performanceLevelId);

    const saveAdjustment = useSaveCalibrationAdjustment(cycleEvaluationId);

    // A new adjustment from the report (a bulk one, a colleague's) replaces the row's copy, unless
    // it is this row's own save coming back, which would undo what was typed since.
    const savedAdjustment = toPendingAdjustment(employee);
    const [draft, setDraft] = useState<PendingAdjustment>(savedAdjustment);
    const [lastSeenAdjustment, setLastSeenAdjustment] = useState(savedAdjustment);
    const [lastSentAdjustment, setLastSentAdjustment] = useState<PendingAdjustment | null>(null);

    if (!isSameAdjustment(savedAdjustment, lastSeenAdjustment)) {
        setLastSeenAdjustment(savedAdjustment);
        const isOwnSave =
            saveAdjustment.isPending ||
            (!!lastSentAdjustment && isSameAdjustment(savedAdjustment, lastSentAdjustment));
        if (!isOwnSave) {
            setDraft(savedAdjustment);
        }
    }

    const { schedule } = useDebouncedSave<PendingAdjustment>({
        delayMs: SAVE_DEBOUNCE_MS,
        onSave: (pending) => {
            setLastSentAdjustment(pending);
            saveAdjustment.mutate({
                employeeId: employee.employeeId,
                request: {
                    calibratedScore: pending.calibratedScore,
                    calibratedPerformanceLevelId: pending.calibratedPerformanceLevelId,
                    comment: pending.comment.length > 0 ? pending.comment : null,
                },
            });
        },
    });

    const persist = (next: Partial<PendingAdjustment>) => {
        const nextDraft = { ...draft, ...next };
        setDraft(nextDraft);
        saveAdjustment.reset();
        schedule(nextDraft);
    };

    // The score and the level are two independent decisions: raising a score does not silently
    // rewrite the level a calibrator explicitly chose, and leaving the level on "no change" keeps
    // the one the manager gave.
    const handleScoreChange = (value: number | null) => persist({ calibratedScore: value });

    const handleLevelChange = (levelId: string | null) =>
        persist({ calibratedPerformanceLevelId: levelId });

    const handleCommentChange = (value: string) => persist({ comment: value });

    // A published row can only ever show a number: an adjustment published without a calibrated
    // score of its own still stands for the submitted one.
    const publishedScore = employee.adjustment?.calibratedScore ?? employee.score;
    const publishedLevelId =
        employee.adjustment?.calibratedPerformanceLevelId ??
        levelForScore({ score: publishedScore, levels })?.id;

    return (
        <tr
            className={'border-base-100 border-b'}
            data-testid={`calibration-employee-row-${employee.employeeId}`}
        >
            <EmployeeIdentityCell
                employeeId={employee.employeeId}
                name={employeeName}
                jobTitle={jobTitle}
                status={employee.adjustment?.status ?? null}
            />

            <td className={TABLE_CELL}>
                <Text color={Colors.BASE_500}>{managerName}</Text>
            </td>

            <td className={TABLE_CELL}>
                <span className={'inline-flex items-baseline gap-1'}>
                    <Text
                        weight={'bold'}
                        color={Colors.NAVY}
                    >
                        {formatScore({ value: employee.score, language })}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {t('calibration.table.percentSymbol')}
                    </Text>
                </span>
            </td>

            <td className={TABLE_CELL}>
                {givenLevel && (
                    <Pills
                        size={'sm'}
                        variant={'default'}
                    >
                        {getLocalizedText(givenLevel.label)}
                    </Pills>
                )}
            </td>

            {isPublished ? (
                <PublishedAdjustmentCells
                    score={publishedScore}
                    level={levels.find((level) => level.id === publishedLevelId)}
                    comment={employee.adjustment?.comment ?? null}
                    language={language}
                />
            ) : (
                <EditableAdjustmentCells
                    employeeId={employee.employeeId}
                    employeeName={employeeName}
                    levels={levels}
                    score={draft.calibratedScore}
                    levelId={draft.calibratedPerformanceLevelId}
                    comment={draft.comment}
                    autosave={saveAdjustment}
                    onScoreChange={handleScoreChange}
                    onLevelChange={handleLevelChange}
                    onCommentChange={handleCommentChange}
                />
            )}
        </tr>
    );
};
